import { useState } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Progress } from '@/components/ui/progress';
import { CheckCircle, XCircle, Trophy, Clock, ArrowRight, RotateCcw } from 'lucide-react';
import { LaTeXRenderer } from './LaTeXRenderer';

interface Question {
  id: string;
  question: string;
  options: string[];
  correct: number;
  explanation: string;
}

interface MicroQuizProps {
  topic: string;
  open: boolean;
  onClose: () => void;
}

// Mock questions for different topics
const questionBank: Record<string, Question[]> = {
  'Chemical Bonding': [
    {
      id: '1',
      question: 'Which type of bond is formed between sodium (Na) and chlorine (Cl)?',
      options: ['Covalent bond', 'Ionic bond', 'Metallic bond', 'Hydrogen bond'],
      correct: 1,
      explanation: 'Sodium (metal) transfers an electron to chlorine (non-metal), forming an ionic bond.'
    },
    {
      id: '2',
      question: 'In $H_2O$, the bond between hydrogen and oxygen is:',
      options: ['Ionic', 'Polar covalent', 'Non-polar covalent', 'Metallic'],
      correct: 1,
      explanation: 'Water has polar covalent bonds due to electronegativity difference between O and H.'
    },
    {
      id: '3',
      question: 'What is the bond angle in a water molecule?',
      options: ['90°', '104.5°', '120°', '180°'],
      correct: 1,
      explanation: 'Due to lone pair repulsion, the bond angle in water is approximately 104.5°.'
    }
  ],
  'Thermodynamics': [
    {
      id: '1',
      question: 'If ΔH is negative, the reaction is:',
      options: ['Endothermic', 'Exothermic', 'Isothermal', 'Adiabatic'],
      correct: 1,
      explanation: 'Negative ΔH means heat is released - an exothermic reaction.'
    },
    {
      id: '2',
      question: 'First law of thermodynamics is essentially:',
      options: ['Conservation of mass', 'Conservation of energy', 'Conservation of momentum', 'Conservation of charge'],
      correct: 1,
      explanation: 'The first law states that energy cannot be created or destroyed, only converted.'
    }
  ],
  'Organic Reactions': [
    {
      id: '1',
      question: 'SN1 reactions favor:',
      options: ['Primary alkyl halides', 'Secondary alkyl halides', 'Tertiary alkyl halides', 'Methyl halides'],
      correct: 2,
      explanation: 'SN1 reactions proceed through carbocation intermediates, which are more stable for tertiary carbons.'
    }
  ]
};

export const MicroQuiz = ({ topic, open, onClose }: MicroQuizProps) => {
  const [currentQ, setCurrentQ] = useState(0);
  const [selected, setSelected] = useState<number | null>(null);
  const [showResult, setShowResult] = useState(false);
  const [score, setScore] = useState(0);
  const [answers, setAnswers] = useState<boolean[]>([]);
  const [finished, setFinished] = useState(false);

  const questions = questionBank[topic] || questionBank['Chemical Bonding'];
  const currentQuestion = questions[currentQ];

  const handleSelect = (index: number) => {
    if (showResult) return;
    setSelected(index);
  };

  const handleCheck = () => {
    if (selected === null) return;
    
    const isCorrect = selected === currentQuestion.correct;
    setShowResult(true);
    setAnswers([...answers, isCorrect]);
    if (isCorrect) setScore(score + 1);
  };

  const handleNext = () => {
    if (currentQ < questions.length - 1) {
      setCurrentQ(currentQ + 1);
      setSelected(null);
      setShowResult(false);
    } else {
      setFinished(true);
    }
  };

  const reset = () => {
    setCurrentQ(0);
    setSelected(null);
    setShowResult(false);
    setScore(0);
    setAnswers([]);
    setFinished(false);
  };

  const progress = ((currentQ + (finished ? 1 : 0)) / questions.length) * 100;

  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent className="max-w-lg">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Trophy className="h-5 w-5 text-orange-500" />
            Boost Score: {topic}
          </DialogTitle>
        </DialogHeader>

        {!finished ? (
          <div className="space-y-4">
            {/* Progress */}
            <div className="flex items-center gap-3">
              <Progress value={progress} className="flex-1" />
              <span className="text-sm text-slate-500">
                {currentQ + 1}/{questions.length}
              </span>
            </div>

            {/* Question */}
            <div className="p-4 bg-slate-50 rounded-xl">
              <LaTeXRenderer 
                content={currentQuestion.question} 
                className="text-lg font-medium text-slate-900"
              />
            </div>

            {/* Options */}
            <div className="space-y-2">
              {currentQuestion.options.map((option, index) => (
                <button
                  key={index}
                  onClick={() => handleSelect(index)}
                  disabled={showResult}
                  className={`w-full p-4 text-left rounded-xl border-2 transition-all ${
                    selected === index
                      ? showResult
                        ? index === currentQuestion.correct
                          ? 'border-green-500 bg-green-50'
                          : 'border-red-500 bg-red-50'
                        : 'border-blue-500 bg-blue-50'
                      : showResult && index === currentQuestion.correct
                      ? 'border-green-500 bg-green-50'
                      : 'border-slate-200 hover:border-slate-300'
                  }`}
                >
                  <div className="flex items-center justify-between">
                    <LaTeXRenderer content={option} />
                    {showResult && index === currentQuestion.correct && (
                      <CheckCircle className="h-5 w-5 text-green-500" />
                    )}
                    {showResult && selected === index && index !== currentQuestion.correct && (
                      <XCircle className="h-5 w-5 text-red-500" />
                    )}
                  </div>
                </button>
              ))}
            </div>

            {/* Explanation */}
            {showResult && (
              <div className={`p-4 rounded-xl ${
                selected === currentQuestion.correct ? 'bg-green-50 border border-green-200' : 'bg-amber-50 border border-amber-200'
              }`}>
                <p className="text-sm">
                  <span className="font-semibold">
                    {selected === currentQuestion.correct ? '✓ Correct!' : '✗ Incorrect'}
                  </span>
                  <br />
                  {currentQuestion.explanation}
                </p>
              </div>
            )}

            {/* Action Button */}
            {!showResult ? (
              <Button 
                className="w-full h-12"
                onClick={handleCheck}
                disabled={selected === null}
              >
                Check Answer
              </Button>
            ) : (
              <Button 
                className="w-full h-12"
                onClick={handleNext}
              >
                {currentQ < questions.length - 1 ? (
                  <>Next Question <ArrowRight className="h-4 w-4 ml-2" /></>
                ) : (
                  'See Results'
                )}
              </Button>
            )}
          </div>
        ) : (
          /* Results Screen */
          <div className="text-center space-y-4">
            <div className="w-20 h-20 bg-gradient-to-br from-orange-400 to-orange-600 rounded-full flex items-center justify-center mx-auto">
              <Trophy className="h-10 w-10 text-white" />
            </div>
            
            <h3 className="text-2xl font-bold text-slate-900">
              Quiz Complete!
            </h3>
            
            <div className="text-4xl font-bold text-orange-500">
              {score}/{questions.length}
            </div>
            
            <p className="text-slate-600">
              {score === questions.length 
                ? 'Perfect score! You\'re a master of this topic!' 
                : score >= questions.length / 2 
                ? 'Good job! Keep practicing to improve further.' 
                : 'Keep practicing! Review the explanations to learn more.'}
            </p>

            <div className="flex gap-2 pt-4">
              <Button variant="outline" className="flex-1" onClick={reset}>
                <RotateCcw className="h-4 w-4 mr-2" />
                Try Again
              </Button>
              <Button className="flex-1" onClick={onClose}>
                Done
              </Button>
            </div>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
};
